import { HelpCircle, Plus } from "lucide-react";
import { Reveal } from "./primitives";

const FAQS = [
  {
    question: "How much does rooftop solar cost?",
    answer:
      "The indicative economics depend on system size, roof conditions, electricity consumption and applicable subsidy. SunShell One helps you get a clearer estimate for your actual home instead of relying on generic averages.",
  },
  {
    question: "How much roof area do I need?",
    answer:
      "As a broad guide, rooftop solar usually needs about 80-100 sq. ft. of shadow-free area per kW. A 3 kW system may need around 240-300 sq. ft., 5 kW around 400-500 sq. ft. and 10 kW around 800-1,000 sq. ft. Final suitability is confirmed during survey.",
  },
  {
    question: "What subsidy may be available?",
    answer:
      "Residential subsidy availability depends on the applicable government scheme, eligibility rules, prevailing limits and regulations. SunShell One can assist with the process, but subsidy approval and amount are not guaranteed.",
  },
  {
    question: "How does PM Surya Ghar work?",
    answer:
      "PM Surya Ghar is a government rooftop solar scheme for eligible residential consumers. Registration, approval and subsidy release follow the official government process. Current details should be checked on the official portal: https://www.pmsuryaghar.gov.in/.",
  },
  {
    question: "Can I finance my solar installation?",
    answer:
      "SunShell One can guide you through available payment and financing routes. Any loan approval, final terms, documentation and sanction remain subject to the lender's process and decision.",
  },
  {
    question: "How does net metering work?",
    answer:
      "With net metering, eligible rooftop solar systems can export surplus electricity to the grid and receive credits as per state regulations. Approval, meter changes and settlement rules depend on the relevant DISCOM.",
  },
  {
    question: "Will my solar work during a power cut?",
    answer:
      "A standard grid-connected solar system shuts down during a power cut for safety. A battery or hybrid setup can provide backup, depending on the system design and selected equipment.",
  },
  {
    question: "Does solar work during the monsoon?",
    answer:
      "Yes. Solar systems continue to generate during the monsoon, but output is lower on cloudy or rainy days. Savings are best understood through annual generation rather than daily generation alone.",
  },
  {
    question: "How long does installation take?",
    answer:
      "The physical installation can often be completed in a few days once site readiness, material availability and approvals are in place. DISCOM approvals, net metering and related timelines vary by location and process.",
  },
  {
    question: "What warranties do I receive?",
    answer:
      "Warranty coverage is provided separately for solar modules, inverter, balance-of-system components and installation workmanship. Exact warranty terms, exclusions and service routes are shared in your project documents.",
  },
  {
    question: "What happens if my generation drops?",
    answer:
      "SunShell One helps you monitor performance and raise service requests when generation appears lower than expected. The support route helps diagnose whether the cause is weather, shading, equipment, grid availability or maintenance needs.",
  },
  {
    question: "What happens to the system if I sell my house?",
    answer:
      "The rooftop solar system is typically transferred along with the property. Any transfer of documents, warranties, DISCOM records or financing obligations should be handled as part of the sale process.",
  },
];

export const FAQ = () => (
  <section id="faq" className="relative overflow-hidden bg-cream py-24 sm:py-32 lg:py-40" data-testid="faq-section">
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        }),
      }}
    />

    <div className="pointer-events-none absolute left-1/2 top-16 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-amber/10 blur-3xl" />
    <div className="pointer-events-none absolute -bottom-28 right-0 h-[420px] w-[420px] rounded-full bg-teal/10 blur-3xl" />

    <div className="relative z-10 mx-auto max-w-[1200px] px-5 sm:px-8">
      <div className="mx-auto max-w-3xl text-center">
        <Reveal>
          <p className="eyebrow mb-6 text-amber">FAQ</p>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="display-serif text-4xl leading-[1.02] tracking-[-0.03em] text-navy sm:text-5xl lg:text-[3.6rem]">
            Practical answers before you start.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-navy/60">
            Clear guidance on cost, roof area, subsidy, approvals, monitoring and long-term ownership.
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.14}>
        <div className="mt-14 grid gap-4 lg:grid-cols-2">
          {FAQS.map((item, index) => (
            <details
              key={item.question}
              className="group rounded-[1.4rem] border border-navy/10 bg-white/75 p-5 shadow-xl shadow-navy/5 backdrop-blur-md transition-[border-color,background-color,transform] duration-300 open:border-amber/35 open:bg-white hover:-translate-y-0.5 hover:border-amber/25"
              data-testid={`faq-item-${index}`}
            >
              <summary className="flex cursor-pointer list-none items-start gap-4 text-left">
                <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-full bg-navy/5 text-amber transition-colors duration-300 group-open:bg-amber group-open:text-white">
                  <HelpCircle size={18} strokeWidth={2} />
                </span>
                <span className="flex-1 text-lg font-semibold leading-snug tracking-tight text-navy">
                  {item.question}
                </span>
                <Plus className="mt-1 shrink-0 text-navy/40 transition-transform duration-300 group-open:rotate-45 group-open:text-amber" size={20} />
              </summary>
              <p className="ml-14 mt-4 max-w-[62ch] text-sm leading-7 text-navy/62 sm:text-base">
                {item.answer}
              </p>
            </details>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);
